import {Inject, Injectable} from '@angular/core';
import {
  AUTH_CONFIG, AuthConfig, AuthenticationAction, AuthError, AuthService,
  GhostMerchantSignUpBountyPayload, SignUpPayload
} from "./auth.service";
import {BehaviorSubject, EMPTY, first, Observable, of} from "rxjs";
import {catchError, distinctUntilChanged, finalize, map, switchMap} from "rxjs/operators";
import {JwtHelper} from './helpers/jwt.helper';
import {HttpHeaders} from '@angular/common/http';
import {Router} from "@angular/router";
import {Role} from './role';
import {RestClientService} from '../rest-client/rest-client.service';
import {AccessToken, AuthenticationTokens, EmailModel, ResetPasswordModel, User} from '../model';


@Injectable({
  providedIn: 'root'
})
export class CustomAuthService extends AuthService {

  readonly TOKEN_SCHEME: string = 'Bearer ';
  readonly LOCAL_STORAGE_AUTHENTICATION_TOKENS: string = "tokens";

  private userUpdated: BehaviorSubject<void> = new BehaviorSubject<void>(undefined);
  private currentUserSubject: BehaviorSubject<User | undefined> = new BehaviorSubject<User | undefined>(undefined);

  constructor(private restClient: RestClientService, private router: Router,
              @Inject(AUTH_CONFIG) private authConfig: AuthConfig) {
    super();
    this.userUpdated.pipe(map(_ => this.getDecodedAccessToken()),
      distinctUntilChanged()).subscribe(decodedAccessToken => {
      if (decodedAccessToken) {
        const user = this.toUser(decodedAccessToken);
        return this.currentUserSubject.next(user);
      }
    });
  }

  override registerWithEmail(payload: SignUpPayload): Observable<void> {
    return this.restClient.post<AuthenticationTokens>('/users', payload).pipe(first(),
      map(tokens => this.storeAuthTokens(tokens)));
  }

  override registerGhostMerchantWithEmail(payload: SignUpPayload): Observable<void> {
    return this.restClient.post<AuthenticationTokens>('/users/ghost-merchant', payload).pipe(first(),
      map(tokens => this.storeAuthTokens(tokens)));
  }

  override registerGhostMerchantWithBounty(payload: GhostMerchantSignUpBountyPayload): Observable<void> {
    return this.restClient.post<AuthenticationTokens>('/users/ghost-merchant-bounty', payload).pipe(first(),
      map(tokens => this.storeAuthTokens(tokens)));
  }

  override login(authAction: AuthenticationAction): Observable<void> {
    return authAction.login().pipe(first(),
      map(tokens => this.storeAuthTokens(tokens)));
  }

  override logout(): Observable<void> {
    const headers = new HttpHeaders({Authorization: this.TOKEN_SCHEME + this.getRefreshToken()});
    this.storeAuthTokens(null);
    return this.restClient.delete<void>('/tokens', {headers})
      .pipe(first(), finalize(() => {
          const _ = this.router.navigate(['/auth/login'])
        }
      ));
  }

  override getCurrentAccessToken(): Observable<string | null> {
    return of(this.getAccessToken());
  }

  override refreshCurrentAccessToken(): Observable<string | null> {
    return of(this.getRefreshToken()).pipe(switchMap(refreshToken => {
        if (refreshToken) {
          const refreshToken = this.getRefreshToken();
          const headers = new HttpHeaders({Authorization: this.TOKEN_SCHEME + refreshToken});
          return this.restClient.get<AuthenticationTokens>('/tokens', {headers})
            .pipe(map(tokens => {
              if (refreshToken) {
                tokens.refreshToken = refreshToken;
              }
              this.storeAuthTokens(tokens);
              return tokens.accessToken;
            }), catchError(error => {
              //TODO add error handle
              throw AuthError.UNKNOWN;
            }));
        } else {
          this.storeAuthTokens(null);
          this.router.navigate([this.authConfig.notAuthenticatedRedirectRoute]);
          return of(null);
        }
      }
    ));
  }

  override reloadUser(): Observable<void> {
    return EMPTY;
  }

  override isCurrentUserVerified(): Observable<boolean> {
    return this.currentUserSubject.pipe(map(currentUser => !!currentUser && currentUser.emailVerified));
  }

  override hasAuthenticatedUser(): boolean {
    return localStorage.getItem(this.LOCAL_STORAGE_AUTHENTICATION_TOKENS) != null;
  }

  override hasAuthenticatedUser$(): Observable<boolean> {
    return this.getCurrentUser$().pipe(map(value => (value !== undefined && value !== null)), distinctUntilChanged());
  }

  sendResetPasswordEmail(email: string): Observable<void> {
    return this.restClient.put<void>('/users/password/forgotten', {email} as EmailModel)
  }

  override sendNewVerifyEmail(): Observable<void> {
    return this.restClient.post<void>(`/users/${this.getCurrentUser()?.id!}/email/verification`, null);
  }


  override resetPassword(code: string, newPassword: string): Observable<void> {
    return this.restClient.post<void>('/users/password/reset', {code, password: newPassword} as ResetPasswordModel)
  }

  getCurrentUser$(): Observable<User | undefined> {
    return this.currentUserSubject.asObservable();
  }

  getCurrentUser(): User | undefined {
    return this.currentUserSubject.value;
  }

  override verifyEmail(code: string): Observable<void> {
    const userId = this.getCurrentUser()!.id;
    return this.restClient.put<void>(`/users/${userId}/email/verification`, {code})
      .pipe(switchMap(() => this.refreshCurrentAccessToken()), map(() => {
        return
      }));
  }

  override userHasRole(role: Role): boolean {
    return this.currentUserSubject.value?.roles.indexOf(role) !== -1;
  }

  private storeAuthTokens(tokens: AuthenticationTokens | null): void {
    if (tokens) {
      localStorage.setItem(this.LOCAL_STORAGE_AUTHENTICATION_TOKENS, JSON.stringify(tokens));
    } else {
      localStorage.removeItem(this.LOCAL_STORAGE_AUTHENTICATION_TOKENS);
    }
    this.userUpdated.next();
  }

  private getDecodedAccessToken(): AccessToken | null {
    const accessToken = this.getAccessToken();
    if (accessToken) {
      return JwtHelper.decodeJwtToken(accessToken);
    } else {
      return null;
    }
  }

  private getAccessToken(): string | null {
    return this.getToken(authTokens => authTokens.accessToken);
  }

  private getRefreshToken(): string | null {
    return this.getToken(authTokens => authTokens.refreshToken);
  }

  private getToken(tokenExtractor: (token: AuthenticationTokens) => string): string | null {
    const authTokens = localStorage.getItem(this.LOCAL_STORAGE_AUTHENTICATION_TOKENS);
    if (authTokens) {
      return tokenExtractor(JSON.parse(authTokens));
    } else {
      return null;
    }
  }

  private toUser(decodedAccessToken: any): User {
    return {
      id: decodedAccessToken.jti,
      email: decodedAccessToken.sub,
      emailVerified: decodedAccessToken.email_verified,
      roles: decodedAccessToken.roles,
      organizationId: decodedAccessToken.organization_id,
    } as User;
  }

}
